import { styled, useTheme } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { ModalComponent } from '../../../../components/ModalComponent/ModalComponent';
import { LoadingId, SelectItem, User, UserRole } from '../../../../types';

import { FormField } from '../../../../components/FormField/FormField';
import { FormValidationMessage } from '../../../../components/FormValidationMessage/FormValidationMessage';
import { SingleSelect } from '../../../../components/SingleSelect/SingleSelect';
import { Switch } from '../../../../components/SwitchButton/Switch';
import { Typography } from '../../../../components/Typography/Typography';
import { AssignProxySkeletonLoader } from '../../../../components/SkeletonLoader/AssignProxy.SkeletonLoader';
import { useLoadingBar } from '../../../../hooks/useLoadingBar';
import { useToastMessage } from '../../../../hooks/useToastMessage';
import { extractErrorMessage } from '../../../../utils/errorNotifier';
import { useAssignProxyToOperatingPartner } from '../../../../queries/hooks/useAssignProxyToOperatingPartner';
import { useUsers } from '../../../../queries/hooks/useUsers';
import { hasRole } from '../../../../utils/roles';
import UserInfo from './UserInfo';

type Props = {
  assignProxyModalOpen: boolean;
  handleAssignProxyModalClose: () => void;
  user: User;
};

const validationMessage = 'Proxy is required field';

export function AssignProxyModal({
  assignProxyModalOpen,
  handleAssignProxyModalClose,
  user,
}: Props) {
  const { colors } = useTheme();
  const { mutate: onAssignProxyUser, isLoading: submitInProgress } =
    useAssignProxyToOperatingPartner();
  const { pushSuccessToast, pushErrorToast } = useToastMessage();
  const { startLoading, stopLoading } = useLoadingBar();
  const { isLoading: areUsersLoading, data: users } = useUsers();

  const [fieldToched, setFieldTouched] = useState(false);
  const [selectedProxy, setSelectedProxy] = useState<SelectItem | null>(null);
  // SVP-820: fill-only is the default, matching how proxies have always worked.
  const [canSubmit, setCanSubmit] = useState(false);

  // SVP-826: a user holding both Submitter and Proxy would otherwise be offered as their own proxy
  // here, and the API refuses that on save — a proxy acts on somebody else's behalf.
  const proxyOptions = useMemo(() => {
    if (!users) return [];
    return users
      .filter((item) => hasRole(item, UserRole.PROXY) && item.id !== user.id)
      .map((item) => ({
        id: item.id,
        value: item.name,
      }));
  }, [users, user.id]);

  const handleAssignProxy = useCallback(() => {
    if (!selectedProxy) return;
    startLoading(LoadingId.assignProxyToUser);
    onAssignProxyUser(
      {
        proxy: Number(selectedProxy.id),
        // Sent in the same request as the proxy: the API resets the permission on a proxy change
        // unless the request says otherwise, so splitting these would wipe the switch again.
        proxyCanSubmit: canSubmit,
        id: user.id,
      },
      {
        onSuccess: () => {
          pushSuccessToast({
            title: 'Proxy Assigned',
            message: `Proxy for ${user.name} Submitter Profile have been successfully assigned`,
          });
          handleAssignProxyModalClose();
        },
        onError: (error: unknown) => {
          pushErrorToast({ message: extractErrorMessage(error) });
        },
        onSettled: () => {
          stopLoading(LoadingId.assignProxyToUser);
        },
      }
    );
  }, [
    canSubmit,
    handleAssignProxyModalClose,
    onAssignProxyUser,
    pushErrorToast,
    pushSuccessToast,
    selectedProxy,
    startLoading,
    stopLoading,
    user.id,
    user.name,
  ]);

  return (
    <ModalComponent
      isOpen={assignProxyModalOpen}
      onClose={handleAssignProxyModalClose}
      actionButtonsProps={{
        isSubmitDisabled: submitInProgress || !selectedProxy,
        onSubmit: handleAssignProxy,
        onCancel: handleAssignProxyModalClose,
        submitText: 'Assign Proxy',
      }}
      title='Assign Proxy'
      top='80px'
    >
      <Content>
        <UserInfo user={user} />
        {areUsersLoading ? (
          <AssignProxySkeletonLoader />
        ) : (
          <>
            <FormField
              label={'Proxy'}
              error={fieldToched && !selectedProxy ? validationMessage : ''}
            >
              <SingleSelect
                listboxStyle={{ height: '195px' }}
                options={proxyOptions}
                value={selectedProxy}
                onChange={(_, option) => {
                  setSelectedProxy(option);
                }}
                onBlur={() => {
                  setFieldTouched(true);
                }}
                disablePortal
                fieldPlaceholder='Select proxy'
              />
            </FormField>
            {selectedProxy && (
              <PermissionRow>
                <Switch checked={canSubmit} onChange={() => setCanSubmit((prev) => !prev)} />
                <div>
                  <Typography variant='h5' color={colors.text.main}>
                    Allow submitting timesheets
                  </Typography>
                  <Typography variant='caption' color={colors.text.caption}>
                    This proxy can view and fill. Turn on to also let them submit.
                  </Typography>
                </div>
              </PermissionRow>
            )}
          </>
        )}
        {!selectedProxy && (
          <FormValidationMessage text='Please, enter the missing details to assign a proxy.' />
        )}
      </Content>
    </ModalComponent>
  );
}

const Content = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PermissionRow = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;
